import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import Sidebar from '../Components/Sidebar/Sidebar';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import verificandoToken from '../../FuncoesGerais/VerificarToken';
import verificandoApp from '../../FuncoesGerais/verificaApp';
import filtrarUsuarioIA from '../../FuncoesGerais/filtrarUsuarioIA';
import { ensureProtocol } from '../../config/backend';
import { getDatasPadrao } from '../../utils/relatorioDatas';
import './relatorioEmpresa.css';

const CORES_GRAFICO = { emAndamento: '#6366f1', concluida: '#10b981' };

function RelatorioEmpresa() {
  const { datInic: defaultIni, datFina: defaultFim } = getDatasPadrao();
  const [data, setData] = useState([]);
  const [datInic, setDatIni] = useState(defaultIni);
  const [datFina, setDatFina] = useState(defaultFim);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [equipeVisualizacao, setEquipeVisualizacao] = useState({ equipe_ids: null, usuarios: [] });
  const areaImpressaoRef = useRef(null);
  const history = useHistory();

  const carregarEquipeVisualizacao = async () => {
    const baseUrl = ensureProtocol(localStorage.getItem('ipBackend2') || '');
    const token = localStorage.getItem('token');
    if (!baseUrl || !token) {
      setEquipeVisualizacao({ equipe_ids: '', usuarios: [] });
      return;
    }
    try {
      const res = await axios.get(baseUrl + '/usuarios/equipe-visualizacao', { headers: { Authorization: token } });
      const d = res.data || {};
      const usuarios = filtrarUsuarioIA(Array.isArray(d.usuarios) ? d.usuarios : [], 'nome');
      setEquipeVisualizacao({
        equipe_ids: d.equipe_ids != null ? d.equipe_ids : '',
        usuarios,
      });
    } catch (e) {
      console.error('Erro ao carregar equipe de visualização:', e);
      setEquipeVisualizacao({ equipe_ids: '', usuarios: [] });
    }
  };

  const verificar = async () => {
    const resultado = await verificandoApp('ATENDENTE');
    if (!resultado) history.push('/');
  };

  function aplicarMascaraData(valor) {
    if (!valor) return '';
    const numeros = valor.replace(/\D/g, '').slice(0, 8);
    if (numeros.length <= 2) return numeros;
    if (numeros.length <= 4) return `${numeros.slice(0, 2)}/${numeros.slice(2)}`;
    return `${numeros.slice(0, 2)}/${numeros.slice(2, 4)}/${numeros.slice(4)}`;
  }

  const imprimir = () => {
    if (areaImpressaoRef.current) {
      const janela = window.open('', '_blank');
      janela.document.write(`
        <html><head><title>Atendimento por empresa</title>
        <style>body{ font-family: Arial,sans-serif; padding: 16px; } table{ border-collapse: collapse; width:100%; } th,td{ border:1px solid #ddd; padding:8px; text-align:left; } th{ background:#f5f5f5; } h1{ margin-bottom:16px; }</style></head><body>
        <h1>Atendimento por empresa</h1>
        <p>Período: ${datInic} a ${datFina}</p>
        ${areaImpressaoRef.current.innerHTML}
        </body></html>`);
      janela.document.close();
      janela.focus();
      setTimeout(() => { janela.print(); janela.close(); }, 300);
    } else window.print();
  };

  const buscarRelatorio = async () => {
    verificar();
    const datIni = (datInic || '').trim();
    const datFin = (datFina || '').trim();
    if (!datIni || !datFin) {
      setErro('Informe a data inicial e a data final para pesquisar.');
      return;
    }
    setErro('');

    const rawEquipe = equipeVisualizacao.equipe_ids != null && equipeVisualizacao.equipe_ids !== undefined
      ? equipeVisualizacao.equipe_ids
      : (localStorage.getItem('equpe') || localStorage.getItem('equipe') || '');
    const equipe = (rawEquipe == null || rawEquipe === undefined || String(rawEquipe).trim() === '' || String(rawEquipe) === 'undefined') ? '' : String(rawEquipe).trim();

    verificandoToken();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(ensureProtocol(localStorage.getItem('ipBackend2')) + '/satis/relatorioEmpresa', {
        headers: { Authorization: token },
        params: {
          datIni: datIni + ' 00:00:00',
          datFin: datFin + ' 23:59:59',
          equipe,
        },
      });
      const rows = Array.isArray(response.data) ? response.data : [];
      setData(rows);
    } catch (error) {
      setData([]);
      setErro(error?.response?.data?.error || 'Erro ao buscar dados.');
      console.error('Erro ao buscar relatório por empresa:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    verificar();
    carregarEquipeVisualizacao();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (equipeVisualizacao.equipe_ids !== null) {
      buscarRelatorio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipeVisualizacao.equipe_ids]);

  const chartData = data.map((r) => ({
    name: r.empresa_cliente || 'Não informada',
    Concluídos: r.concluidos ?? 0,
    'Em andamento': r.em_andamento ?? 0,
    total: r.total_chamados ?? 0,
  }));

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content">
        <div className="container-fluid titulo relatorio-empresa">
          <div className="page-header no-print">
            <h1>Atendimento por empresa</h1>
            <p className="text-muted small mb-2">
              Agrupa chamados abertos no período pela empresa/CNPJ cadastrada no solicitante.
            </p>
            <div className="toolbar flex-wrap">
              <div className="input-group" style={{ maxWidth: 420 }}>
                <input
                  value={datInic}
                  onChange={(e) => setDatIni(aplicarMascaraData(e.target.value.toUpperCase()))}
                  type="text"
                  className="form-control"
                  placeholder="Data Inicial"
                />
                <input
                  value={datFina}
                  onChange={(e) => setDatFina(aplicarMascaraData(e.target.value.toUpperCase()))}
                  type="text"
                  className="form-control"
                  placeholder="Data Final"
                />
                <button onClick={buscarRelatorio} className="btn btn-secondary" type="button" disabled={loading}>
                  <i className="fas fa-search" /> {loading ? 'Carregando...' : 'Pesquisar'}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={imprimir} title="Imprimir">
                  <i className="fas fa-print" /> Imprimir
                </button>
              </div>
            </div>
          </div>
          {erro && <div className="alert alert-warning no-print">{erro}</div>}
          {loading && data.length === 0 && <p className="text-muted no-print">Carregando relatório...</p>}
          {!loading && data.length === 0 && !erro && (
            <p className="text-muted no-print">Informe o período e clique em Pesquisar ou aguarde o carregamento.</p>
          )}
          {data.length > 0 && (
            <>
              <div className="relatorio-grafico no-print" style={{ height: 340, marginBottom: 24 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 16, right: 16, left: 8, bottom: 72 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" angle={-22} textAnchor="end" height={88} interval={0} tick={{ fontSize: 11, fill: '#334155', fontFamily: 'inherit' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }} />
                    <Legend iconType="square" iconSize={10} />
                    <Bar dataKey="Em andamento" stackId="a" fill={CORES_GRAFICO.emAndamento} />
                    <Bar dataKey="Concluídos" stackId="a" fill={CORES_GRAFICO.concluida} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div ref={areaImpressaoRef} className="table-responsive">
                <table className="table table-bordered table-hover tabela-relatorio-empresa">
                  <thead>
                    <tr>
                      <th>Empresa / CNPJ</th>
                      <th>Total chamados</th>
                      <th>Concluídos</th>
                      <th>Em andamento</th>
                      <th>Horas trabalhadas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((r) => (
                      <tr key={r.empresa_cliente}>
                        <td>{r.empresa_cliente}</td>
                        <td>{r.total_chamados ?? 0}</td>
                        <td>{r.concluidos ?? 0}</td>
                        <td>{r.em_andamento ?? 0}</td>
                        <td>{r.horas_trabalhadas_texto || formatMin(r.horas_trabalhadas_min)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function formatMin(min) {
  if (min == null || min === 0) return '0min';
  const m = Number(min);
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}min` : `${m}min`;
}

export default RelatorioEmpresa;
